import { status } from '@sportninja/common/constants/attendanceStatus'
import { formatCalendarMonthDay } from '@sportninja/common/ts-utils/date-utils'
import type {
  GameRosterPlayer,
  PlayerRosterStatus,
  RosterStatusContext,
} from '../types'

const SUSPENSION_OPEN_END = 'Indefinitely'

/**
 * The one status a roster row shows, highest priority first: starting goalie,
 * suspended, injured, then (once the game is over) playing / away, and finally
 * the player's availability answer.
 *
 * The first three show to everyone; availability only to viewers allowed to
 * see attendance (`null` → an empty cell).
 */
export const getPlayerRosterStatus = (
  player: GameRosterPlayer,
  { canViewAttendanceInfo, isGameStarted, isGameCompleted }: RosterStatusContext
): PlayerRosterStatus | null => {
  if (
    player.player_type?.is_goalie === 1 &&
    player.is_starting &&
    player.is_playing
  ) {
    return { kind: 'starting_goalie' }
  }
  if (player.is_suspended && player.suspension) {
    return {
      kind: 'suspended',
      startDate: formatCalendarMonthDay(player.suspension.start_date),
      endDate:
        formatCalendarMonthDay(player.suspension.end_date) ||
        SUSPENSION_OPEN_END,
    }
  }
  if (player.is_injured) {
    return { kind: 'injured' }
  }
  if (isGameCompleted && isGameStarted) {
    return { kind: player.is_playing ? 'playing' : 'away' }
  }
  if (!canViewAttendanceInfo) {
    return null
  }
  switch (player.attendance_status) {
    case status.ATTENDING:
      return { kind: 'available' }
    case status.MAYBE:
      return { kind: 'maybe' }
    case status.NOT_ATTENDING:
      return { kind: 'not_available' }
    default:
      return { kind: 'no_response' }
  }
}
